/**
 * Cart audit trail (UAT E.01): removing a line or clearing the cart is logged
 * server-side via POST /sale/pos-events.
 *
 * FIRE-AND-FORGET, by contract: this never throws, never returns a promise the
 * caller must handle, and never blocks the cart. An offline till, a 429 from the
 * rate limiter, a 404 from an older backend — all swallowed.
 *
 * The API module is loaded with a dynamic import so the cart store stays free
 * of the axios/env chain at import time (env.ts throws when VITE_API_URL is
 * unset, which is the case in a bare CI test run).
 */
import { useAuthStore } from '@/stores/auth.store';
import type { PosEventItem, PosEventType } from '@/types/sale';

export interface ReportableLine {
  productId: string;
  name: string;
  unitPrice: number;
  /** A seat's own `unitPrice` is its slot price (time-of-day rate), when set. */
  seats: Array<{ unitPrice?: number }>;
}

export interface ReportableBundle {
  bundleId: string;
  name: string;
  unitPrice: number;
  quantity: number;
}

/** Seats priced in different slots are reported at their average price. */
export function itemFromLine(l: ReportableLine): PosEventItem {
  const qty = l.seats.length;
  const total = l.seats.reduce(
    (s, seat) => s + (typeof seat?.unitPrice === 'number' ? seat.unitPrice : l.unitPrice),
    0,
  );
  return {
    productId: l.productId,
    productName: l.name,
    qty,
    unitPrice: qty > 0 ? Math.round(total / qty) : l.unitPrice,
  };
}

/** A bundle is reported with its bundleId in `productId`. */
export function itemFromBundle(b: ReportableBundle): PosEventItem {
  return { productId: b.bundleId, productName: b.name, qty: b.quantity, unitPrice: b.unitPrice };
}

export function reportPosEvent(type: PosEventType, items: PosEventItem[], note?: string): void {
  try {
    const list = items.filter((i) => i && i.qty > 0);
    if (list.length === 0) return;
    // No session → nothing to attribute it to (and no point provoking a 401).
    if (!useAuthStore.getState().accessToken) return;
    import('@/api/sale.api')
      .then((m) => m.postPosEvent({ type, items: list, ...(note ? { note } : {}) }))
      .catch(() => {
        /* audit is best-effort; the cart must never notice */
      });
  } catch {
    /* never let audit break the cart */
  }
}
